// src/services/errorHandler.ts
import { logger } from '../utils/logger';
// Centralized error handling with user-friendly messages and retry logic

export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  PERMISSION = 'permission',
  VALIDATION = 'validation',
  NOT_FOUND = 'not_found',
  SERVER = 'server',
  UNKNOWN = 'unknown',
}

export interface AppError {
  type: ErrorType;
  message: string;
  userMessage: string;
  canRetry: boolean;
  originalError?: any;
}

/**
 * Specific error messages for common operations
 */
const ErrorMessages = Object.freeze({
  IMAGE_UPLOAD_FAILED: 'Failed to upload image. Please check your connection and try again.',
  IMAGE_TOO_LARGE: 'Image is too large. Please select an image under 5MB.',
  MESSAGE_SEND_FAILED: 'Failed to send message. Please try again.',
  CONNECTION_REQUEST_FAILED: 'Failed to send connection request. Please try again.',
  LOAD_DATA_FAILED: 'Failed to load data. Please pull down to refresh.',
  PROFILE_UPDATE_FAILED: 'Failed to update profile. Please try again.',
  MATCH_FAILED: 'Failed to process match. Please try again.',
  CAMERA_PERMISSION_DENIED: 'Camera permission denied. Please enable it in your device settings.',
  STORAGE_PERMISSION_DENIED: 'Storage permission denied. Please enable it in your device settings.',
  NETWORK_OFFLINE: 'You appear to be offline. Please check your internet connection.',
});

// Export after definition to avoid TDZ issues
export { ErrorMessages };

/**
 * Convert any error into a user-friendly AppError
 */
export function handleError(error: any, context?: string): AppError {
  // Network errors
  if (error.message?.includes('Failed to fetch') ||
      error.message?.includes('Network request failed') ||
      error.code === 'NETWORK_ERROR') {
    return {
      type: ErrorType.NETWORK,
      message: `Network error${context ? ` in ${context}` : ''}`,
      userMessage: 'Unable to connect. Please check your internet connection and try again.',
      canRetry: true,
      originalError: error,
    };
  }

  // Authentication errors
  if (error.message?.includes('JWT') ||
      error.message?.includes('auth') ||
      error.status === 401) {
    return {
      type: ErrorType.AUTHENTICATION,
      message: `Authentication error${context ? ` in ${context}` : ''}`,
      userMessage: 'Your session has expired. Please log in again.',
      canRetry: false,
      originalError: error,
    };
  }

  // Permission errors
  if (error.status === 403 || error.message?.includes('permission')) {
    return {
      type: ErrorType.PERMISSION,
      message: `Permission error${context ? ` in ${context}` : ''}`,
      userMessage: 'You don\'t have permission to do that.',
      canRetry: false,
      originalError: error,
    };
  }

  // Not found errors
  if (error.status === 404 || error.message?.includes('not found')) {
    return {
      type: ErrorType.NOT_FOUND,
      message: `Resource not found${context ? ` in ${context}` : ''}`,
      userMessage: 'We couldn\'t find what you\'re looking for. It may have been deleted.',
      canRetry: false,
      originalError: error,
    };
  }

  // Validation errors
  if (error.status === 400 || error.message?.includes('invalid')) {
    return {
      type: ErrorType.VALIDATION,
      message: `Validation error${context ? ` in ${context}` : ''}`,
      userMessage: error.message || 'Please check your input and try again.',
      canRetry: false,
      originalError: error,
    };
  }

  // Server errors
  if (error.status >= 500 || error.message?.includes('server error')) {
    return {
      type: ErrorType.SERVER,
      message: `Server error${context ? ` in ${context}` : ''}`,
      userMessage: 'Something went wrong on our end. Please try again in a moment.',
      canRetry: true,
      originalError: error,
    };
  }

  // Unknown errors
  return {
    type: ErrorType.UNKNOWN,
    message: error.message || `Unknown error${context ? ` in ${context}` : ''}`,
    userMessage: 'Something went wrong. Please try again.',
    canRetry: true,
    originalError: error,
  };
}

/**
 * Retry an async operation with exponential backoff
 */
export async function retryWithBackoff<T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  initialDelay: number = 1000
): Promise<{ success: boolean; data?: T; error?: AppError }> {
  let lastError: any;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const result = await operation();
      return { success: true, data: result };
    } catch (error) {
      lastError = error;

      // Don't retry if it's not a retryable error
      const appError = handleError(error);
      if (!appError.canRetry) {
        return { success: false, error: appError };
      }

      // Don't wait after the last attempt
      if (attempt < maxRetries) {
        const delay = initialDelay * Math.pow(2, attempt);
        logger.log(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // All retries exhausted
  const appError = handleError(lastError);
  return { success: false, error: appError };
}

/**
 * Show a user-friendly error toast
 */
export function showErrorToast(error: AppError, toastCallback: (message: string, color: string) => void) {
  toastCallback(error.userMessage, 'danger');
}

/**
 * Check if user is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Wait for network connection
 */
export function waitForConnection(timeout: number = 10000): Promise<boolean> {
  return new Promise((resolve) => {
    if (isOnline()) {
      resolve(true);
      return;
    }

    const timeoutId = setTimeout(() => {
      window.removeEventListener('online', onlineHandler);
      resolve(false);
    }, timeout);

    const onlineHandler = () => {
      clearTimeout(timeoutId);
      window.removeEventListener('online', onlineHandler);
      resolve(true);
    };

    window.addEventListener('online', onlineHandler);
  });
}
